import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AppMark } from '@/components/layout/app-switcher';
import { CopyButton } from '@/components/shared/copy-button';
import { DeleteAppDialog } from '@/components/shared/delete-app-dialog';
import { EmptyState } from '@/components/shared/empty-state';
import { TableSkeleton } from '@/components/shared/loading';
import { PageHeader } from '@/components/shared/page-header';
import { SectionCard } from '@/components/shared/section-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import type { App, Page } from '@/lib/types';

export function AppsList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<App | null>(null);
  const q = useQuery({ queryKey: ['apps'], queryFn: () => api<Page<App>>('/admin/v1/apps', { query: { limit: 200 } }) });

  // The list endpoint has no search parameter; up to 200 apps are loaded, so filter in the browser.
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = q.data?.data ?? [];
    return term ? all.filter((a) => [a.name, a.slug, a.id, a.description ?? ''].some((v) => v.toLowerCase().includes(term))) : all;
  }, [q.data, search]);
  const total = q.data?.data.length ?? 0;

  const newButton = <Button asChild><Link to="/apps/new"><Plus /> New application</Link></Button>;
  return (
    <div className="space-y-6">
      <PageHeader title="Applications" description="Each application has its own credentials, devices and API keys." actions={newButton} />

      <SectionCard
        flush
        title={
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, slug or ID…" className="pr-8 pl-8 font-normal" aria-label="Search applications" />
            {search && (
              <Button variant="ghost" size="icon-xs" className="absolute top-1/2 right-1.5 -translate-y-1/2" onClick={() => setSearch('')} aria-label="Clear search"><X /></Button>
            )}
          </div>
        }
        actions={!q.isLoading && <span className="text-sm text-muted-foreground tabular-nums">{search ? `${rows.length} of ${total}` : total} {total === 1 ? 'application' : 'applications'}</span>}
      >
        {q.isLoading ? <TableSkeleton rows={4} /> : total === 0 ? (
          <EmptyState icon={LayoutGrid} title="No applications yet" hint="Create one to upload push credentials and issue API keys." action={newButton} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Search} title="No applications match" hint={<>Nothing matches “{search}”.</>} action={<Button variant="outline" size="sm" onClick={() => setSearch('')}>Clear search</Button>} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">Application</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>App ID</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="w-20 pr-5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} className="group cursor-pointer" onClick={() => navigate(`/apps/${a.id}`)}>
                  <TableCell className="pl-5">
                    <Link to={`/apps/${a.id}`} className="flex items-center gap-3 outline-none" onClick={(e) => e.stopPropagation()}>
                      <AppMark name={a.name} logo={a.logo} />
                      <span className="grid leading-tight">
                        <span className="font-medium group-hover:text-primary">{a.name}</span>
                        <span className="font-mono text-xs text-muted-foreground">{a.slug}</span>
                      </span>
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground"><div className="max-w-xs truncate">{a.description ?? '—'}</div></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <span className="inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground">{a.id.slice(0, 10)}… <CopyButton value={a.id} label={false} /></span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(a.created_at)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(a.updated_at)}</TableCell>
                  <TableCell className="pr-5" onClick={(e) => e.stopPropagation()}>
                    <span className="flex justify-end gap-1">
                      <Button asChild variant="ghost" size="icon-sm" aria-label={`Edit ${a.name}`} title="Edit">
                        <Link to={`/apps/${a.id}/settings`}><Pencil /></Link>
                      </Button>
                      <Button variant="ghost" size="icon-sm" className="text-destructive hover:text-destructive" aria-label={`Delete ${a.name}`} title="Delete" onClick={() => setDeleting(a)}>
                        <Trash2 />
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
      <DeleteAppDialog app={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}
